import type { TreeOption } from 'naive-ui';

/**
 * 为一份 UI 结构树建立 key → 节点索引，展示字段变化时保留原树与节点引用。
 *
 * 索引接收调用方当前的树引用，若是 Vue 响应式纯数据则保留其代理；不接收 Object3D。
 */
export class FeatureTreePresentationIndex {
  /** 当前结构树引用，仅在全量投影或结构更新时替换。 */
  private tree: readonly TreeOption[] | null = null;
  /** key 到当前节点的只读定位索引。 */
  private readonly nodeByKey = new Map<string, TreeOption>();
  /** 累计结构索引构建次数。 */
  private builds = 0;

  /**
   * 定位当前结构树中的节点，重复字段更新不重新遍历。
   * @param tree 调用方当前的树，必须是实际写入 UI 的引用。
   * @param key 稳定节点 key。
   * @returns 对应节点；不存在时为空。
   */
  public resolve(tree: readonly TreeOption[], key: string): TreeOption | undefined {
    if (this.tree !== tree) {
      this.nodeByKey.clear();
      this.tree = tree;
      const pending = [...tree].reverse();
      while (pending.length > 0) {
        const node = pending.pop()!;
        if (typeof node.key === 'string' || typeof node.key === 'number') this.nodeByKey.set(String(node.key), node);
        if (Array.isArray(node.children)) {
          for (let index = node.children.length - 1; index >= 0; index--) pending.push(node.children[index]);
        }
      }
      this.builds++;
    }
    return this.nodeByKey.get(key);
  }

  /** @returns 显式调试读取的统计，不在定位循环创建对象。 */
  public getStats(): FeatureTreePresentationIndexStats {
    return { nodes: this.nodeByKey.size, builds: this.builds };
  }

  /** 解除旧树和节点引用，不销毁调用方 UI 数据。 */
  public clear(): void {
    this.tree = null;
    this.nodeByKey.clear();
  }
}
